import React from 'react';
import { UserEmail } from '../../hooks/useUserEmails';
import { 
  formatEmailDisplay, 
  getEmailStatusText, 
  getEmailStatusColor,
  formatLastUsed,
  canRemoveEmail
} from '../../utils/email';
import { CheckCircleIcon, XCircleIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { TrashIcon, PencilIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

interface EmailListProps {
  emails: UserEmail[];
  onRemove: (emailId: string) => void;
  onEdit: (emailId: string) => void;
  onResend: (emailId: string) => void;
  isLoading?: boolean;
  workExperiences?: Array<{ id: string; company: string; position: string; }>;
}

export function EmailList({ emails, onRemove, onEdit, onResend, isLoading, workExperiences = [] }: EmailListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg animate-pulse">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!emails || emails.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p>No email addresses found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {emails.map((email) => (
        <EmailItem
          key={email.id}
          email={email}
          onRemove={onRemove}
          onEdit={onEdit}
          onResend={onResend}
          workExperiences={workExperiences}
        />
      ))}
    </div>
  );
}

interface EmailItemProps {
  email: UserEmail;
  onRemove: (emailId: string) => void;
  onEdit: (emailId: string) => void;
  onResend: (emailId: string) => void;
  workExperiences: Array<{ id: string; company: string; position: string; }>;
}

function EmailItem({ email, onRemove, onEdit, onResend, workExperiences }: EmailItemProps) {
  const statusColor = getEmailStatusColor(email.is_verified, email.is_active);
  const statusText = getEmailStatusText(email.is_verified, email.is_active, email.bounce_type);
  const canDelete = canRemoveEmail(email.is_primary);

  const getStatusIcon = () => {
    if (!email.is_verified) {
      return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
    if (!email.is_active) {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <h4 className="text-base font-medium text-gray-900 dark:text-white">
              {formatEmailDisplay(email.email, email.label)}
            </h4>
            {email.is_primary && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                Primary
              </span>
            )}
          </div>
          
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 space-y-1">
            <p className={statusColor}>{statusText}</p>
            {email.is_verified && email.last_used_at && (
              <p>Last used: {formatLastUsed(email.last_used_at)}</p>
            )}
            <p>Added: {new Date(email.created_at).toLocaleDateString()}</p>
            {email.work_experience_id && (
              <p className="text-xs">
                Linked to: {workExperiences.find(we => we.id === email.work_experience_id)?.position} at{' '}
                {workExperiences.find(we => we.id === email.work_experience_id)?.company}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-4">
          {!email.is_verified && (
            <button
              onClick={() => onResend(email.id)}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Resend verification email"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          )}
          
          {email.is_verified && (
            <button
              onClick={() => onEdit(email.id)}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Edit email settings"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
          
          {canDelete && (
            <button
              onClick={() => {
                if (window.confirm(`Are you sure you want to remove ${email.email}?`)) {
                  onRemove(email.id);
                }
              }}
              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              title="Remove email"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {email.bounce_type && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Email Bounced
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                This email address is no longer receiving messages. 
                {email.bounce_type === 'hard_bounce' && ' The address may not exist.'}
                {email.bounce_type === 'soft_bounce' && ' The inbox may be full or temporarily unavailable.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}